import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { productsAPI } from '@/utils/api';
import { toast } from 'sonner';
import { Sparkles, ArrowLeft, ShoppingCart, Clock, Package } from 'lucide-react';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [priceHistory, setPriceHistory] = useState([]);

  useEffect(() => {
    fetchProduct();
    fetchPriceHistory();
  }, [id]);

  const fetchProduct = async () => {
    try {
      const response = await productsAPI.getById(id);
      setProduct(response.data);
    } catch (error) {
      toast.error('Failed to fetch product');
      navigate('/products');
    } finally {
      setLoading(false);
    }
  };

  const fetchPriceHistory = async () => {
    try {
      const response = await productsAPI.getPriceHistory(id);
      setPriceHistory(response.data || []);
    } catch (error) {
      console.log('No price history available');
    }
  };

  const addToCart = () => {
    const cart = JSON.parse(localStorage.getItem('cart') || '[]');
    cart.push({ ...product, quantity: 1 });
    localStorage.setItem('cart', JSON.stringify(cart));
    toast.success('Added to cart!');
    navigate('/cart');
  };

  const calculateTimeLeft = (endTime) => {
    if (!endTime) return null;
    const diff = new Date(endTime) - new Date();
    if (diff <= 0) return 'ENDED';
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#1A3A52]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      {/* Navbar */}
      <nav className="border-b border-[#E5E1DC] bg-white/90 backdrop-blur-md">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
              <Sparkles className="h-7 w-7 text-[#1A3A52]" strokeWidth={1.5} />
              <span className="text-2xl font-serif font-semibold text-[#2C3E50]">LuxLyx</span>
            </div>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-6 py-12">
        <Button
          data-testid="back-btn"
          variant="ghost"
          onClick={() => navigate('/products')}
          className="mb-8 text-[#5A6C7D] hover:text-[#1A3A52] hover:bg-[#F5F3F0]"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Products
        </Button>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Images */}
          <div>
            <div className="relative aspect-square rounded-xl overflow-hidden bg-white border border-[#E5E1DC] mb-4">
              <img
                src={product.images[selectedImage] || 'https://via.placeholder.com/600'}
                alt={product.name}
                className="w-full h-full object-cover"
              />
              {product.flash_sale_end && (
                <div className="absolute top-6 right-6 bg-[#2C3E50] text-white px-4 py-2 rounded-full text-sm font-medium flex items-center space-x-2">
                  <Clock className="h-4 w-4" />
                  <span>{calculateTimeLeft(product.flash_sale_end)}</span>
                </div>
              )}
              <div className="absolute top-6 left-6 bg-[#1A3A52] text-white px-4 py-2 rounded-full text-sm font-semibold">
                {Math.round(product.discount_percent)}% OFF
              </div>
            </div>
            
            {/* Thumbnail Gallery */}
            {product.images.length > 1 && (
              <div className="grid grid-cols-4 gap-4">
                {product.images.map((img, idx) => (
                  <div
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`aspect-square rounded-lg overflow-hidden cursor-pointer border-2 ${selectedImage === idx ? 'border-[#1A3A52]' : 'border-[#E5E1DC]'}`}
                  >
                    <img src={img} alt={`${product.name} ${idx + 1}`} className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            <h1 className="text-4xl font-serif font-semibold mb-4 text-[#2C3E50]">{product.name}</h1>
            <p className="text-[#5A6C7D] text-lg mb-6 leading-relaxed">{product.description}</p>
            
            <div className="bg-white border border-[#E5E1DC] rounded-xl p-6 mb-6">
              <div className="flex items-baseline space-x-4 mb-2">
                <div className="text-5xl font-serif font-semibold text-[#1A3A52]">${product.current_price}</div>
                <div className="text-2xl text-[#8B9AAC] line-through">${product.mrp}</div>
              </div>
              <div className="text-lg text-[#5A6C7D]">
                Save ${(product.mrp - product.current_price).toFixed(2)} ({Math.round(product.discount_percent)}%)
              </div>
            </div>

            <div className="flex items-center space-x-6 mb-8">
              <div className="flex items-center space-x-2">
                <Package className="h-5 w-5 text-[#5A6C7D]" strokeWidth={1.5} />
                <span className="text-[#5A6C7D]">{product.stock} available</span>
              </div>
              <div className="px-4 py-1.5 bg-[#1A3A52]/5 border border-[#1A3A52]/10 rounded-full">
                <span className="text-[#1A3A52] text-sm font-medium">{product.category}</span>
              </div>
            </div>

            <Button
              data-testid="add-to-cart-detail"
              onClick={addToCart}
              size="lg"
              className="w-full bg-[#1A3A52] text-white hover:bg-[#2D4F6B] font-medium text-lg py-6"
            >
              <ShoppingCart className="mr-2 h-5 w-5" strokeWidth={1.5} />
              Add to Cart
            </Button>

            <div className="mt-8 bg-white border border-[#E5E1DC] rounded-xl p-6">
              <h3 className="font-serif font-semibold text-lg mb-3 text-[#2C3E50]">Product Details</h3>
              <div className="space-y-2 text-[#5A6C7D]">
                <div className="flex justify-between">
                  <span>SKU:</span>
                  <span className="font-medium text-[#2C3E50]">{product.sku}</span>
                </div>
                <div className="flex justify-between">
                  <span>Category:</span>
                  <span className="font-medium text-[#2C3E50]">{product.category}</span>
                </div>
                <div className="flex justify-between">
                  <span>Condition:</span>
                  <span className="font-medium text-[#2C3E50]">Brand New</span>
                </div>
              </div>
            </div>

            {/* Price History */}
            {priceHistory.length > 0 && (
              <div className="mt-6 bg-white border border-[#E5E1DC] rounded-xl p-6">
                <h3 className="font-serif font-semibold text-lg mb-4 flex items-center text-[#2C3E50]">
                  <Clock className="h-5 w-5 mr-2 text-[#1A3A52]" strokeWidth={1.5} />
                  Price History
                </h3>
                <div className="space-y-3">
                  {priceHistory.slice(0, 5).map((history, index) => (
                    <div
                      key={index}
                      className="flex justify-between items-center py-2 border-b border-[#E5E1DC] last:border-0"
                    >
                      <div className="text-sm text-[#5A6C7D]">
                        {new Date(history.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </div>
                      <div className="text-lg font-serif font-semibold text-[#1A3A52]">
                        ${history.price.toFixed(2)}
                      </div>
                    </div>
                  ))}
                </div>
                {priceHistory.length > 5 && (
                  <div className="mt-3 text-center text-sm text-[#8B9AAC]">
                    + {priceHistory.length - 5} more price changes
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
